<template>
  <div class="relative">
    <!-- Opener Button -->
    <button class="dropdown-opener" :class="{ 'active': expanded, 'no-shadow': noButtonShadow }"
    @click="expanded = !expanded">
      <div class="w-5 h-5 flex items-center justify-center">
        <FacebookPlatformIcon :width="16" :height="16" />
      </div>
      <div class="flex items-center gap-1 px-1">
        <BaseText type="label" size="sm" class="text-text-muted truncate" style="max-width: 396px;">
          {{ selectedAdvertiser.name }}
        </BaseText>
        <BaseText type="body" size="sm" class="text-text-subdued">
          {{ selectedAdvertiser.advertiserId }}
        </BaseText>
      </div>
      <button class="w-5 h-5 flex items-center justify-center transition-colors duration-100 text-text-normal hover:text-text-muted"
      @click.stop="openAdvertiserUrl(selectedAdvertiser.url)">
        <OpenExternalIcon :width="16" :height="16" />
      </button>
      <div class="transform transition-transform duration-100" :style="{ transform: expanded ? 'scaleY(-1)' : 'scaleY(1)' }">
        <ChevronIcon className="text-icon-normal" />
      </div>
    </button>
    <!-- Dropdown Menu -->
    <transition>
      <div v-if="expanded" class="dropdown-menu" v-on-clickaway="() => { expanded = false }">
        <div class="dropdown-scroller flex flex-col gap-1 p-1">
          <button v-for="option in options" :key="`advertiser-option-${option.advertiserId}`"
          class="w-full flex gap-2 p-1.5 rounded-md transition-colors duration-100 min-w-0 hover:bg-neutral-50" 
          :class="{ 'bg-neutral-25': option.selected }" @click="selectAdvertiser(option.advertiserId)">
            <div class="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <FacebookPlatformIcon :width="16" :height="16" />
            </div>
            <div class="flex-grow flex flex-col gap-1 text-left min-w-0">
              <div class="w-full flex items-center gap-1">
                <BaseText type="label" size="sm" class="flex-shrink text-text-muted truncate">
                  {{ option.name ?? option.advertiserId }}
                </BaseText>
                <button class="w-5 h-5 flex items-center justify-center transition-colors duration-100 text-text-normal hover:text-text-muted flex-shrink-0"
                @click.stop="openAdvertiserUrl(option.url)">
                  <OpenExternalIcon :width="16" :height="16" />
                </button>
              </div>
              <BaseText v-if="option.name" type="body" size="sm" class="w-full text-text-normal truncate">
                {{ option.advertiserId }}
              </BaseText>
            </div>
            <div class="w-5 flex-shrink-0">
              <CheckmarkIcon v-if="option.selected" class="text-icon-normal" />
            </div>
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway2'

import FacebookPlatformIcon from '../globals/Icons/FilterIcons/Platforms/FacebookPlatformIcon.vue'
import OpenExternalIcon from '../globals/Icons/OpenExternalIcon.vue'
import CheckmarkIcon from '../globals/Icons/NewCheckmarkIcon.vue'
import ChevronIcon from '../globals/Icons/ChevronIcon.vue'

export default {
  name: 'AdvertiserSelectionDropdown',
  mixins: [clickaway],
  components: {
    FacebookPlatformIcon,
    OpenExternalIcon,
    CheckmarkIcon,
    ChevronIcon
  },
  props: {
    selectedAdvertiserId: {
      type: String,
      required: true
    },
    advertiserOptions: {
      type: Array,
      required: true
    },
    noButtonShadow: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      expanded: false
    }
  },
  computed: {
    selectedAdvertiser () {
      return this.advertiserOptions.find(option => option.advertiserId === this.selectedAdvertiserId)
    },
    options () {
      return this.advertiserOptions.map(option => ({
        ...option,
        selected: option.advertiserId === this.selectedAdvertiserId
      }))
    }
  },
  methods: {
    selectAdvertiser (advertiserId) {
      if (advertiserId !== this.selectedAdvertiserId) {
        this.$emit('update:selectedAdvertiserId', advertiserId)
      }
      this.expanded = false
    },
    openAdvertiserUrl (url) {
      window.open(url, '_blank')
    }
  }
}
</script>

<style scoped>
.dropdown-opener {
  display: flex;
  gap: 2px;
  align-items: center;
  border-radius: 6px;
  padding: 6px;
  background-color: white;
  box-shadow: 0 2px 4px -1.5px rgba(24, 35, 68, 0.04), 0 1px 2px -0.75px rgba(24, 35, 68, 0.04), 0 0 0 1px rgba(0, 40, 136, 0.08);
  transition: background-color 100ms ease-in-out, box-shadow 100ms ease-in-out;
}
.dropdown-opener:hover:not(.active) {
  box-shadow: 0 2px 4px -1.5px rgba(24, 35, 68, 0.08), 0 1px 2px -0.75px rgba(24, 35, 68, 0.08), 0 0 0 1px rgba(0, 40, 136, 0.12);
}
.dropdown-opener.active {
  background-color: #F6F8FA;
  box-shadow: none;
}
/* Shadowless variant - falls back to a hover background, since without the shadow
   there's nothing left to indicate the opener is interactive */
.dropdown-opener.no-shadow,
.dropdown-opener.no-shadow:hover:not(.active) {
  box-shadow: none;
}
.dropdown-opener.no-shadow:hover:not(.active) {
  background-color: #F6F8FA;
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0px;
  width: 324px;
  box-shadow: 0 1px 2px 0 rgba(4, 26, 75, 0.13), 0 0 0 1px rgba(0, 56, 108, 0.08);
  border-radius: 10px;
  background-color: white;
  z-index: 50000;
  overflow: hidden;
}
.dropdown-scroller {
  max-height: 304px;
  overflow-y: auto;
}
.dropdown-scroller::-webkit-scrollbar {
  width: 3px;
}
.dropdown-scroller::-webkit-scrollbar-thumb {
  background-color: #DFE1E7;
  border-radius: 18px;
}

.v-enter-active, .v-leave-active {
  transition: opacity 100ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
</style>